import { FC, ReactNode } from 'react'

/**
 * Some MCP clients (e.g. Claude) register without a `logo_uri` or canonical
 * `client_name`, so we supply our own name + brand mark for ones we recognise.
 * Matched on the `redirect_uri` host — the only stable signal (`client_id` is a
 * Stytch-assigned id that varies).
 */
export type KnownOAuthClient = {
  name: string
  logo: ReactNode
}

const ClaudeLogo: FC = () => (
  <svg
    data-testid="consent.known-client-logo"
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="#D97757"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Claude"
  >
    <path d="M4.709 15.955l4.72-2.647.079-.23-.079-.128h-.23l-.785-.048-2.683-.072-2.327-.097-2.254-.121-.568-.121L0 11.784l.054-.35.477-.32.683.06 1.51.103 2.262.157 1.64.096 2.43.255h.387l.054-.157-.133-.097-.103-.097-2.354-1.595-2.547-1.685-1.334-.97-.72-.491-.363-.46-.157-1.006.654-.72.878.06.224.061.89.685 1.903 1.474 2.485 1.83.364.303.145-.103.018-.072-.163-.272-1.353-2.446-1.444-2.49-.643-1.032-.17-.62a2.97 2.97 0 01-.103-.733L6.587.198 6.985 0l.96.13.403.349.598 1.366.968 2.157 1.504 2.929.44.868.235.803.088.246h.152V9.6l.122-1.65.228-2.026.222-2.608.076-.733.363-.879.72-.477.565.272.466.66-.066.422-.28 1.823-.546 2.831-.355 1.896h.207l.237-.236.96-1.274 1.613-2.016.713-.804.831-.885.534-.422h1.009l.744 1.106-.334 1.142-1.045 1.324-.866 1.124-1.244 1.674-.776 1.339.072.107.185-.018 2.824-.602 1.527-.272 1.819-.314.823.387.092.39-.327.794-1.964.487-2.305.46-3.435.819-.042.03.047.06 1.547.146.662.034h1.618l3.014.225.788.521.474.633-.078.484-1.213.625-1.637-.387-3.825-.91-1.312-.327h-.182v.11l1.094 1.07 2.004 1.81 2.508 2.33.127.578-.32.46-.34-.05-2.207-1.66-.851-.748-1.927-1.62h-.127v.169l.443.65 2.346 3.526.122 1.084-.17.354-.607.213-.667-.122-1.371-1.927-1.413-2.166-1.143-1.944-.139.08-.674 7.27-.317.37-.728.28-.607-.461-.322-.748.322-1.474.388-1.922.314-1.526.286-1.897.171-.629-.012-.042-.139.018-1.426 1.957-2.17 2.933-1.716 1.838-.412.163-.713-.37.066-.66.398-.586 2.371-3.016 1.43-1.868.922-1.077-.006-.157h-.054L4.42 18.5l-1.286.166-.552-.516.066-.847.262-.276 2.166-1.49-.006.006z" />
  </svg>
)

const ChatGPTLogo: FC = () => (
  <svg
    data-testid="consent.known-client-logo"
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="ChatGPT"
  >
    <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
  </svg>
)

const KNOWN_OAUTH_CLIENTS: ReadonlyArray<{
  matchesHost: (host: string) => boolean
  client: KnownOAuthClient
}> = [
  {
    matchesHost: host => host === 'claude.ai' || host.endsWith('.claude.ai'),
    client: { name: 'Claude', logo: <ClaudeLogo /> }
  },
  {
    matchesHost: host =>
      host === 'chatgpt.com' || host.endsWith('.chatgpt.com'),
    client: { name: 'ChatGPT', logo: <ChatGPTLogo /> }
  }
]

// Returns undefined for relative/invalid URIs (e.g. local previews), mirroring
// the `deriveWebsite` guard in ConsentScreen.tsx.
export const matchKnownOAuthClient = (
  redirectUri: string | null
): KnownOAuthClient | undefined => {
  if (!redirectUri) {
    return undefined
  }

  try {
    const { host } = new URL(redirectUri)
    return KNOWN_OAUTH_CLIENTS.find(({ matchesHost }) => matchesHost(host))
      ?.client
  } catch {
    return undefined
  }
}
